/**
 * Cart Rendering Module
 *
 * Handles HTML generation for the side cart panel (slide-out drawer).
 * This is separate from cart-panel.js which handles cart cards in chat messages.
 */

import { escapeHtml } from '../../utils/helpers.js';

/**
 * Generate HTML for a cart item in the side cart panel
 * @param {Object} item - Cart item data from Shopify
 * @param {Object} item.key - Unique cart item key
 * @param {Object} item.title - Product title
 * @param {Object} item.variant_title - Variant title
 * @param {Object} item.price - Price in cents
 * @param {Object} item.compare_at_price - Compare at price in cents (optional)
 * @param {Object} item.quantity - Quantity
 * @param {Object} item.image - Image URL
 * @returns {string} HTML string
 */
export function createSideCartItemHTML(item) {
    const originalPrice = item.compare_at_price ? (item.compare_at_price / 100) : null;
    const currentPrice = item.price / 100;
    const isOnSale = originalPrice && originalPrice > currentPrice;
    const title = escapeHtml(item.title || 'Product');
    const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? escapeHtml(item.variant_title)
        : '';

    return `
        <div class="fyncall-cart-item-image">
            ${item.image
                ? `<img src="${escapeHtml(item.image)}" alt="${title}">`
                : '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:24px;">📦</div>'}
        </div>
        <div class="fyncall-cart-item-info">
            <div class="fyncall-cart-item-name">${title}</div>
            ${variantTitle ? `<div class="fyncall-cart-item-variant">${variantTitle}</div>` : ''}
            <div class="fyncall-cart-item-price">
                $${currentPrice.toFixed(2)}
                ${isOnSale ? `<span style="font-size:11px;color:#9ca3af;text-decoration:line-through;margin-left:6px;">$${originalPrice.toFixed(2)}</span>` : ''}
            </div>
            <div class="fyncall-cart-item-actions">
                <div class="fyncall-cart-item-qty">
                    <button data-action="decrease" data-key="${escapeHtml(item.key)}">−</button>
                    <span>${item.quantity}</span>
                    <button data-action="increase" data-key="${escapeHtml(item.key)}">+</button>
                </div>
                <button class="fyncall-cart-item-remove" data-action="remove" data-key="${escapeHtml(item.key)}">🗑️</button>
                <button class="fyncall-cart-item-save" data-action="save" data-title="${title}">Save for later</button>
            </div>
        </div>
    `;
}

/**
 * Create a cart item element with event bindings
 * @param {Object} item - Cart item data
 * @param {Object} callbacks - Event callbacks
 * @param {Function} callbacks.onQuantityChange - (action, key, currentQty) => void
 * @param {Function} callbacks.onSaveForLater - (title) => void
 * @returns {HTMLElement}
 */
export function createSideCartItem(item, callbacks = {}) {
    const div = document.createElement('div');
    div.className = 'fyncall-cart-item';
    div.innerHTML = createSideCartItemHTML(item);

    // Bind quantity change events
    div.querySelectorAll('button[data-action]').forEach(btn => {
        btn.onclick = (e) => {
            const action = e.target.dataset.action;
            if (action === 'save') {
                if (callbacks.onSaveForLater) {
                    callbacks.onSaveForLater(e.target.dataset.title);
                }
            } else {
                const key = e.target.dataset.key;
                if (callbacks.onQuantityChange) {
                    callbacks.onQuantityChange(action, key, item.quantity);
                }
            }
        };
    });

    return div;
}

/**
 * Generate HTML for empty cart state in side panel
 * @returns {string} HTML string
 */
export function createEmptySideCartHTML() {
    return `
        <div class="fyncall-cart-empty">
            <div class="fyncall-cart-empty-icon">🛒</div>
            <div class="fyncall-cart-empty-text">Your cart is empty</div>
        </div>
    `;
}

/**
 * Generate HTML for default promo code form
 * @returns {string} HTML string
 */
export function createPromoFormHTML() {
    return `
        <input type="text" class="fyncall-cart-promo-input" id="fyncall-cart-promo-input" placeholder="Enter code">
        <button class="fyncall-cart-promo-btn" id="fyncall-cart-promo-btn">Apply</button>
    `;
}

/**
 * Generate HTML for checkout button in chat
 * @returns {string} HTML string
 */
export function createCheckoutButtonHTML() {
    return `
        <button class="fyncall-checkout-btn" onclick="window.FyncallWidgetSDK.handleCheckout()">
            <span class="fyncall-checkout-icon">🛒</span>
            <span class="fyncall-checkout-text">Proceed to Checkout</span>
            <span class="fyncall-checkout-arrow">→</span>
        </button>
    `;
}

/**
 * Get checkout button styles
 * @returns {string} CSS styles
 */
export function getCheckoutButtonStyles() {
    return `
        .fyncall-checkout-button-container {
            display: flex;
            justify-content: center;
            padding: 12px 16px;
            margin: 8px 0;
        }
        .fyncall-checkout-btn {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 14px 28px;
            background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
            color: white;
            border: none;
            border-radius: 25px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
        }
        .fyncall-checkout-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
            background: linear-gradient(135deg, #45a049 0%, #3d8b40 100%);
        }
        .fyncall-checkout-btn:active {
            transform: translateY(0);
        }
        .fyncall-checkout-icon {
            font-size: 20px;
        }
        .fyncall-checkout-arrow {
            font-size: 18px;
            transition: transform 0.3s ease;
        }
        .fyncall-checkout-btn:hover .fyncall-checkout-arrow {
            transform: translateX(4px);
        }
    `;
}

/**
 * Inject checkout button styles if not already present
 */
export function injectCheckoutStyles() {
    if (!document.getElementById('fyncall-checkout-styles')) {
        const style = document.createElement('style');
        style.id = 'fyncall-checkout-styles';
        style.textContent = getCheckoutButtonStyles();
        document.head.appendChild(style);
    }
}

/**
 * Create checkout button container element
 * @param {Function} onCheckout - Checkout callback
 * @returns {HTMLElement}
 */
export function createCheckoutButton(onCheckout) {
    injectCheckoutStyles();

    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'fyncall-checkout-button-container';
    buttonContainer.innerHTML = createCheckoutButtonHTML();

    if (onCheckout) {
        const btn = buttonContainer.querySelector('.fyncall-checkout-btn');
        btn.onclick = onCheckout;
        // Remove inline onclick if using callback
        btn.removeAttribute('onclick');
    }

    return buttonContainer;
}

/**
 * Render cart items in a container
 * @param {Array} items - Cart items
 * @param {HTMLElement} container - Container element
 * @param {Object} callbacks - Event callbacks
 * @param {Function} callbacks.onQuantityChange - (action, key, currentQty) => void
 * @param {Function} callbacks.onSaveForLater - (title) => void
 * @returns {number} Total savings from compare_at_price
 */
export function renderSideCartItems(items, container, callbacks = {}) {
    container.innerHTML = '';
    let totalSavings = 0;

    items.forEach(item => {
        const itemEl = createSideCartItem(item, callbacks);
        container.appendChild(itemEl);

        // Calculate savings from compare_at_price if available
        if (item.compare_at_price && item.compare_at_price > item.price) {
            totalSavings += (item.compare_at_price - item.price) * item.quantity / 100;
        }
    });

    return totalSavings;
}

/**
 * Update side cart panel visibility based on cart state
 * @param {Object} elements - Widget elements
 * @param {Object} options - Display options
 * @param {boolean} options.hasItems - Whether cart has items
 * @param {number} options.cartCount - Number of items
 * @param {number} options.totalSavings - Total product savings
 * @param {number} options.promoDiscount - Applied promo discount
 * @param {number} options.subtotal - Cart subtotal
 */
export function updateSideCartDisplay(elements, options) {
    const { hasItems, cartCount, totalSavings = 0, promoDiscount = 0, subtotal = 0 } = options;

    if (!hasItems) {
        elements.cartItems.innerHTML = createEmptySideCartHTML();
        elements.cartFooter.style.display = 'none';
        elements.cartPromo.style.display = 'none';
        elements.cartShippingProgress.style.display = 'none';
        elements.cartPanelCount.textContent = '0';
        elements.headerCartBadge.classList.remove('show');
        return;
    }

    // Show cart sections
    elements.cartPromo.style.display = 'block';
    elements.cartFooter.style.display = 'block';

    // Update savings display
    const totalSavingsAmount = totalSavings + promoDiscount;
    if (totalSavingsAmount > 0) {
        elements.cartSavings.style.display = 'flex';
        elements.cartSavingsAmount.textContent = '$' + totalSavingsAmount.toFixed(2);
    } else {
        elements.cartSavings.style.display = 'none';
    }

    // Update order summary
    const total = subtotal - promoDiscount;
    elements.cartSubtotal.textContent = '$' + subtotal.toFixed(2);
    elements.cartTotal.textContent = '$' + total.toFixed(2);

    // Show discount row if promo applied
    if (promoDiscount > 0) {
        elements.cartDiscountRow.style.display = 'flex';
        elements.cartDiscount.textContent = '-$' + promoDiscount.toFixed(2);
    } else {
        elements.cartDiscountRow.style.display = 'none';
    }

    // Update counts
    elements.cartPanelCount.textContent = cartCount;
    elements.headerCartBadge.textContent = cartCount;
    elements.headerCartBadge.classList.add('show');
}
